import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import BackOfficeMenu from '../components/BackOfficeMenu';
import './DetailDemandePrixPage.css';

const API_URL = import.meta.env.VITE_API_URL;

const DetailDemandePrixPage = ({ onNavigate, onLogout, currentPage = 'demandes-prix' }) => {
    const [searchParams] = useSearchParams();
    const demandeId = searchParams.get('id') || searchParams.get('demandeId');
    const navigate = useNavigate();

    const [demande, setDemande] = useState(null);
    const [matrix, setMatrix] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'reponses', 'comparaison', 'selection'

    // Form state for adding quote response
    const [selectedSupplierForReponse, setSelectedSupplierForReponse] = useState('');
    const [referenceDevisFournisseur, setReferenceDevisFournisseur] = useState('');
    const [dateDevisFournisseur, setDateDevisFournisseur] = useState(new Date().toISOString().split('T')[0]);
    const [delaiLivraison, setDelaiLivraison] = useState('');
    const [disponibilite, setDisponibilite] = useState('');
    const [commentaire, setCommentaire] = useState('');
    const [pieceJointeFile, setPieceJointeFile] = useState(null);
    const [reponseLignes, setReponseLignes] = useState([]);
    const [submittingReponse, setSubmittingReponse] = useState(false);
    const [reponseSuccessMsg, setReponseSuccessMsg] = useState('');

    // Selection state
    const [selectedOffresMap, setSelectedOffresMap] = useState({}); // { [produitId]: { reponseId, versionNumber, fournisseurId, ligne } }
    const [savingSelection, setSavingSelection] = useState(false);
    const [converting, setConverting] = useState(false);
    const [conversionSuccessMsg, setConversionSuccessMsg] = useState('');

    // PDF download states
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadingSupplierPdfId, setDownloadingSupplierPdfId] = useState(null);

    // Email dispatch state
    const [sendingEmails, setSendingEmails] = useState(false);

    const handleDownloadPDF = async (supplierId = null) => {
        if (supplierId) {
            setDownloadingSupplierPdfId(supplierId);
        } else {
            setDownloadingPdf(true);
        }
        try {
            const token = localStorage.getItem('token');
            const url = supplierId
                ? `${API_URL}/demandes-prix-fournisseurs/${demandeId}/telecharger-pdf?fournisseurId=${supplierId}`
                : `${API_URL}/demandes-prix-fournisseurs/${demandeId}/telecharger-pdf`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const blob = new Blob([response.data], { type: 'application/pdf' });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            let fileName = `DemandePrix_${demande?.reference || 'DP'}.pdf`;
            if (supplierId) {
                const supp = (demande?.fournisseurs || []).find(f => String(f._id) === String(supplierId));
                const suppName = supp?.nom || supp?.raisonSociale || 'fournisseur';
                fileName = `DemandePrix_${demande?.reference || 'DP'}_${suppName.replace(/[^a-zA-Z0-9_-]/g, '_')}.pdf`;
            }

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Erreur lors du téléchargement du PDF:', err);
            alert('Erreur lors de la génération / du téléchargement du document PDF.');
        } finally {
            setDownloadingPdf(false);
            setDownloadingSupplierPdfId(null);
        }
    };

    const handleSendEmailToSupplier = async (supplierId = null) => {
        let confirmMsg = '';
        if (supplierId) {
            const supplier = demande?.fournisseurs?.find(f => f._id === supplierId);
            if (!supplier) return;
            if (!supplier.email) {
                alert(`Le fournisseur ${supplier.nom || supplier.raisonSociale} n'a pas d'adresse email renseignée.`);
                return;
            }
            confirmMsg = `Êtes-vous sûr de vouloir envoyer la demande de prix par email à ${supplier.nom || supplier.raisonSociale} (${supplier.email}) ?`;
        } else {
            const suppliersWithEmail = (demande?.fournisseurs || []).filter(f => f.email);
            if (suppliersWithEmail.length === 0) {
                alert("Aucun fournisseur n'a d'adresse email renseignée.");
                return;
            }
            const emailList = suppliersWithEmail.map(f => `- ${f.nom || f.raisonSociale} : ${f.email}`).join('\n');
            confirmMsg = `Êtes-vous sûr de vouloir envoyer la demande de prix par email aux ${suppliersWithEmail.length} fournisseurs suivants ?\n\n${emailList}`;
        }

        if (!window.confirm(confirmMsg)) {
            return;
        }

        setSendingEmails(true);
        try {
            const token = localStorage.getItem('token');
            const payload = supplierId
                ? { sendEmailMap: { [supplierId]: true } }
                : { supplierIds: (demande?.fournisseurs || []).filter(f => f.email).map(f => f._id) };

            const res = await axios.post(`${API_URL}/demandes-prix-fournisseurs/${demandeId}/envoyer-email`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert(res.data?.message || 'Emails traités avec succès.');
            fetchDemandeDetails();
        } catch (err) {
            console.error('Erreur envoi d\'emails:', err);
            alert(err.response?.data?.message || 'Erreur lors de l\'envoi des emails.');
        } finally {
            setSendingEmails(false);
        }
    };

    useEffect(() => {
        if (demandeId) {
            fetchDemandeDetails();
            fetchComparisonMatrix();
        }
    }, [demandeId]);

    const fetchDemandeDetails = async () => {
        setLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/demandes-prix-fournisseurs/${demandeId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const data = res.data;
            setDemande(data);

            // Populate initial reponse lines form based on requested products
            if (data && data.produits) {
                const initialLines = data.produits.map(p => ({
                    produitId: p.produit?._id || p.produit,
                    nomProduit: p.produit?.nomProduit || 'Produit',
                    reference: p.produit?.reference || '',
                    quantiteDemandee: p.quantite,
                    prixUnitaireHT: p.produit?.prixAchat || 0,
                    remisePercentage: 0,
                    remisePercentage2: 0,
                    tvaPercentage: p.produit?.tva !== undefined ? p.produit.tva : 19,
                    delaiProduit: '',
                    disponibiliteProduit: '',
                    remarque: ''
                }));
                setReponseLignes(initialLines);
            }

            // Populate selected offers map if already saved
            if (data && data.offresSelectionnees && data.offresSelectionnees.length > 0) {
                const map = {};
                data.offresSelectionnees.forEach(off => {
                    const pId = off.produit?._id || off.produit;
                    map[pId] = {
                        produitId: pId,
                        fournisseurId: off.fournisseur?._id || off.fournisseur,
                        reponseId: off.reponseId,
                        versionNumber: off.versionNumber,
                        quantite: off.quantite,
                        prixUnitaireHT: off.prixUnitaireHT,
                        remisePercentage: off.remisePercentage,
                        remisePercentage2: off.remisePercentage2,
                        prixNetHT: off.prixNetHT,
                        tvaPercentage: off.tvaPercentage
                    };
                });
                setSelectedOffresMap(map);
            }
        } catch (err) {
            console.error('Erreur détails demande de prix:', err);
            setError('Impossible de charger la demande de prix.');
        } finally {
            setLoading(false);
        }
    };

    const fetchComparisonMatrix = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/demandes-prix-fournisseurs/${demandeId}/comparaison`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMatrix(res.data?.matrix || []);
        } catch (err) {
            console.error('Erreur matrice comparaison:', err);
        }
    };

    const handleUpdateReponseLine = (produitId, field, val) => {
        setReponseLignes(prev => prev.map(line => {
            if (String(line.produitId) === String(produitId)) {
                return { ...line, [field]: val };
            }
            return line;
        }));
    };

    const handleAddReponseSubmit = async (e) => {
        e.preventDefault();
        if (!selectedSupplierForReponse) {
            alert('Veuillez choisir le fournisseur qui a transmis ce devis.');
            return;
        }

        setSubmittingReponse(true);
        setReponseSuccessMsg('');
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();

            const dataObj = {
                fournisseurId: selectedSupplierForReponse,
                referenceDevisFournisseur,
                dateDevisFournisseur,
                delaiLivraison,
                disponibilite,
                commentaire,
                lignes: reponseLignes.map(l => ({
                    produit: l.produitId,
                    prixUnitaireHT: Number(l.prixUnitaireHT) || 0,
                    remisePercentage: Number(l.remisePercentage) || 0,
                    remisePercentage2: Number(l.remisePercentage2) || 0,
                    tvaPercentage: Number(l.tvaPercentage) !== undefined ? Number(l.tvaPercentage) : 19,
                    quantite: Number(l.quantiteDemandee) || 1,
                    delaiProduit: l.delaiProduit || '',
                    disponibiliteProduit: l.disponibiliteProduit || '',
                    remarque: l.remarque || ''
                }))
            };

            formData.append('data', JSON.stringify(dataObj));
            if (pieceJointeFile) {
                formData.append('pieceJointeDevis', pieceJointeFile);
            }

            const res = await axios.post(`${API_URL}/demandes-prix-fournisseurs/${demandeId}/reponses`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setReponseSuccessMsg(res.data?.message || 'Réponse enregistrée avec succès!');
            setReferenceDevisFournisseur('');
            setCommentaire('');
            setPieceJointeFile(null);
            fetchDemandeDetails();
            fetchComparisonMatrix();
        } catch (err) {
            console.error('Erreur enregistrement réponse fournisseur:', err);
            alert(err.response?.data?.message || 'Erreur lors de l\'enregistrement de la réponse.');
        } finally {
            setSubmittingReponse(false);
        }
    };

    const handleSelectOfferForProduct = (produitId, offer) => {
        if (!offer || !offer.ligne) return;

        const updated = {
            ...selectedOffresMap,
            [produitId]: {
                produitId,
                fournisseurId: offer.fournisseur._id || offer.fournisseur,
                reponseId: offer.reponseId,
                versionNumber: offer.versionNumber,
                quantite: offer.ligne.quantiteDemandee || 1,
                prixUnitaireHT: offer.ligne.prixUnitaireHT,
                remisePercentage: offer.ligne.remisePercentage,
                remisePercentage2: offer.ligne.remisePercentage2,
                prixNetHT: offer.ligne.prixNetHT,
                tvaPercentage: offer.ligne.tvaPercentage
            }
        };
        setSelectedOffresMap(updated);
    };

    const handleSaveSelections = async () => {
        const offresList = Object.values(selectedOffresMap);
        if (offresList.length === 0) {
            alert('Veuillez sélectionner au moins une offre.');
            return;
        }

        setSavingSelection(true);
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/demandes-prix-fournisseurs/${demandeId}/selection-offres`, {
                offres: offresList
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            alert('Sélection des offres enregistrée avec succès.');
            fetchDemandeDetails();
        } catch (err) {
            console.error('Erreur sauvegarde sélection:', err);
            alert('Erreur lors de la sauvegarde des sélections.');
        } finally {
            setSavingSelection(false);
        }
    };

    const handleConvertBC = async (fournisseurId, targetDocType = 'bon_commande') => {
        const typeLabels = {
            'bon_commande': 'Bon de Commande Fournisseur',
            'devis': 'Devis Fournisseur',
            'bon_livraison': 'Bon de Livraison Fournisseur'
        };
        const label = typeLabels[targetDocType] || 'Bon de Commande Fournisseur';

        if (!window.confirm(`Voulez-vous générer automatiquement le ${label} pour cette sélection ?`)) {
            return;
        }

        setConverting(true);
        setConversionSuccessMsg('');
        try {
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/demandes-prix-fournisseurs/${demandeId}/convertir`, {
                fournisseurId,
                documentType: targetDocType
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setConversionSuccessMsg(res.data?.message || `${label} créé avec succès.`);
            fetchDemandeDetails();
        } catch (err) {
            console.error('Erreur conversion document:', err);
            alert(err.response?.data?.message || `Erreur lors de la création du ${label}.`);
        } finally {
            setConverting(false);
        }
    };

    if (loading) {
        return (
            <div className="backoffice-layout">
                <BackOfficeMenu currentPage={currentPage} onNavigate={onNavigate} onLogout={onLogout} />
                <div className="backoffice-content">
                    <div className="dp-detail-loading">
                        <i className="fas fa-spinner fa-spin fa-2x"></i>
                        <p>Chargement des détails de la demande de prix...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !demande) {
        return (
            <div className="backoffice-layout">
                <BackOfficeMenu currentPage={currentPage} onNavigate={onNavigate} onLogout={onLogout} />
                <div className="backoffice-content">
                    <div className="dp-detail-error">
                        <i className="fas fa-exclamation-triangle fa-2x"></i>
                        <p>{error || 'Demande de prix introuvable.'}</p>
                        <button className="btn-back" onClick={() => onNavigate('fournisseurs')}>
                            Retour aux fournisseurs
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const getStatusBadge = (statut) => {
        const classMap = {
            'Brouillon': 'statut-brouillon',
            'Envoyée': 'statut-envoyee',
            'En attente de réponse': 'statut-attente',
            'Réponse reçue': 'statut-recue',
            'Partiellement répondue': 'statut-partiel',
            'Terminée': 'statut-terminee',
            'Annulée': 'statut-annulee'
        };
        return <span className={`dp-status-badge ${classMap[statut] || ''}`}>{statut}</span>;
    };

    return (
        <div className="backoffice-layout">
            <BackOfficeMenu currentPage={currentPage} onNavigate={onNavigate} onLogout={onLogout} />

            <div className="backoffice-content">
                {/* Header Banner */}
                <div className="dp-detail-header-banner">
                    <div className="header-left">
                        <button className="btn-back-link" onClick={() => onNavigate('demandes-prix')}>
                            <i className="fas fa-arrow-left"></i> Demandes de Prix
                        </button>
                        <h2>
                            <i className="fas fa-file-invoice-dollar" style={{ color: '#3b82f6', marginRight: '12px' }}></i>
                            Demande de Prix {demande.reference}
                        </h2>
                        <div className="header-meta">
                            <span><i className="far fa-calendar-alt"></i> Créée le {new Date(demande.dateDemande).toLocaleDateString('fr-FR')}</span>
                            {demande.dateLimiteReponse && (
                                <span><i className="far fa-clock"></i> Limite: {new Date(demande.dateLimiteReponse).toLocaleDateString('fr-FR')}</span>
                            )}
                            {getStatusBadge(demande.statut)}
                        </div>
                    </div>

                    <div className="header-actions">
                        <button
                            type="button"
                            className="btn-download-pdf-header"
                            onClick={() => handleDownloadPDF(null)}
                            disabled={downloadingPdf}
                            title="Télécharger le document PDF de la demande de prix (sans envoi d'email)"
                        >
                            <i className={`fas ${downloadingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                            <span>{downloadingPdf ? 'Génération...' : 'Télécharger PDF'}</span>
                        </button>
                        {demande.documentFournisseurCree && (
                            <button
                                className="btn-doc-created"
                                onClick={() => {
                                    if (onNavigate) {
                                        onNavigate('fournisseur-detail', {
                                            id: demande.documentFournisseurCree?.fournisseurId || demande.fournisseurs[0]?._id,
                                            bcId: demande.documentFournisseurCree?._id
                                        });
                                    }
                                }}
                            >
                                <i className="fas fa-check-circle"></i> Voir Bon de Commande ({demande.documentFournisseurCree.numDocument || 'BC'})
                            </button>
                        )}
                    </div>
                </div>

                {/* Tabs Bar */}
                <div className="dp-tabs-bar">
                    <button
                        className={`dp-tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <i className="fas fa-info-circle"></i> 1. Aperçu & Produits
                    </button>
                    <button
                        className={`dp-tab-btn ${activeTab === 'reponses' ? 'active' : ''}`}
                        onClick={() => setActiveTab('reponses')}
                    >
                        <i className="fas fa-reply-all"></i> 2. Réponses Fournisseurs ({demande.reponses?.length || 0})
                    </button>
                    <button
                        className={`dp-tab-btn ${activeTab === 'comparaison' ? 'active' : ''}`}
                        onClick={() => setActiveTab('comparaison')}
                    >
                        <i className="fas fa-balance-scale"></i> 3. Comparaison des Offres
                    </button>
                    <button
                        className={`dp-tab-btn ${activeTab === 'selection' ? 'active' : ''}`}
                        onClick={() => setActiveTab('selection')}
                    >
                        <i className="fas fa-check-double"></i> 4. Sélection & Conversion
                    </button>
                </div>

                {/* Tab Contents */}
                <div className="dp-tab-content-container">

                    {/* TAB 1: OVERVIEW */}
                    {activeTab === 'overview' && (
                        <div className="dp-tab-pane">
                            <div className="dp-overview-grid">
                                <div className="dp-card">
                                    <div className="dp-suppliers-card-header">
                                        <h4><i className="fas fa-truck"></i> Fournisseurs Sollicités ({demande.fournisseurs.length})</h4>
                                        <button
                                            type="button"
                                            className="btn-action-email-all"
                                            onClick={() => handleSendEmailToSupplier(null)}
                                            disabled={sendingEmails}
                                        >
                                            <i className={`fas ${sendingEmails ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                                            Envoyer à tous par email
                                        </button>
                                    </div>
                                    <div className="suppliers-list">
                                        {demande.fournisseurs.map(f => {
                                            const emailLogs = (demande.envoiEmails || []).filter(e => String(e.fournisseur?._id || e.fournisseur) === String(f._id));
                                            const lastLog = emailLogs.length > 0 ? emailLogs[emailLogs.length - 1] : null;

                                            return (
                                                <div key={f._id} className="supplier-card-item dp-supplier-item-row">
                                                    <div className="dp-supplier-item-left">
                                                        <div className="supplier-avatar">
                                                            <i className="fas fa-building"></i>
                                                        </div>
                                                        <div className="supplier-info">
                                                            <strong>{f.nom || f.raisonSociale}</strong>
                                                            <span>Code: {f.code || 'N/A'} | Tel: {f.telephone || f.mobile || 'N/A'}</span>
                                                            <span>Email: {f.email || 'N/A'}</span>

                                                            {/* Status Badge */}
                                                            <div className="dp-supplier-email-badge-container">
                                                                {lastLog ? (
                                                                    lastLog.statut === 'Envoyé' ? (
                                                                        <span className="email-status-pill-sent">
                                                                            <i className="fas fa-check-circle"></i>
                                                                            Email envoyé le {new Date(lastLog.dateEnvoi).toLocaleString('fr-FR')}
                                                                        </span>
                                                                    ) : (
                                                                        <span className="email-status-pill-error">
                                                                            <i className="fas fa-exclamation-triangle"></i>
                                                                            Échec: {lastLog.erreur || 'Erreur envoi'}
                                                                        </span>
                                                                    )
                                                                ) : (
                                                                    <span className="email-status-pill-none">
                                                                        <i className="fas fa-info-circle"></i>
                                                                        Email non envoyé
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="dp-supplier-actions-group">
                                                        <button
                                                            type="button"
                                                            className="btn-download-single-pdf"
                                                            onClick={() => handleDownloadPDF(f._id)}
                                                            disabled={downloadingSupplierPdfId === f._id}
                                                            title={`Télécharger le PDF pour ${f.nom || f.raisonSociale} sans envoyer d'email`}
                                                        >
                                                            <i className={`fas ${downloadingSupplierPdfId === f._id ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                                                            {downloadingSupplierPdfId === f._id ? 'PDF...' : 'PDF'}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className={`btn-send-single-email ${f.email ? 'has-email' : 'no-email'}`}
                                                            onClick={() => handleSendEmailToSupplier(f._id)}
                                                            disabled={sendingEmails || !f.email}
                                                        >
                                                            <i className={`fas ${sendingEmails ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                                                            {lastLog && lastLog.statut === 'Envoyé' ? 'Renvoyer email' : 'Envoyer par email'}
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="dp-card">
                                    <h4><i className="fas fa-comment-alt"></i> Remarques & Instructions</h4>
                                    <p style={{ color: '#475569', fontSize: '0.95rem' }}>
                                        {demande.remarque || 'Aucune instruction spécifique saisie.'}
                                    </p>

                                    <h4 style={{ marginTop: '20px' }}><i className="fas fa-history"></i> Historique des statuts</h4>
                                    <div className="status-timeline">
                                        {(demande.historiqueStatut || []).map((h, i) => (
                                            <div key={i} className="timeline-item">
                                                <div className="timeline-dot"></div>
                                                <div className="timeline-details">
                                                    <strong>{h.statut}</strong> - {new Date(h.date).toLocaleString('fr-FR')}
                                                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>{h.commentaire}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="dp-card" style={{ marginTop: '20px' }}>
                                <div className="dp-card-header-with-actions">
                                    <h4><i className="fas fa-boxes"></i> Produits Demandés ({demande.produits.length})</h4>
                                    <button
                                        type="button"
                                        className="btn-download-pdf-secondary"
                                        onClick={() => handleDownloadPDF(null)}
                                        disabled={downloadingPdf}
                                        title="Télécharger le document PDF de la demande"
                                    >
                                        <i className={`fas ${downloadingPdf ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                                        Télécharger le PDF de la demande
                                    </button>
                                </div>
                                <table className="dp-table">
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Référence</th>
                                            <th>Désignation Produit</th>
                                            <th>Quantité Demandée</th>
                                            <th>Prix Achat Repère</th>
                                            <th>Remarque</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {demande.produits.map((item, idx) => (
                                            <tr key={idx}>
                                                <td style={{ width: '60px' }}>
                                                    {item.produit?.image ? (
                                                        <img src={`${API_URL.replace('/api', '')}${item.produit.image}`} alt="" className="prod-img-thumb" />
                                                    ) : (
                                                        <div className="prod-img-placeholder"><i className="fas fa-box"></i></div>
                                                    )}
                                                </td>
                                                <td><code>{item.produit?.reference || 'N/A'}</code></td>
                                                <td><strong>{item.produit?.nomProduit || 'Produit'}</strong></td>
                                                <td>
                                                    <span className="qty-pill">{item.quantite} {item.unite || 'unité'}</span>
                                                    {Array.isArray(item.decoupesLineaires) && item.decoupesLineaires.length > 0 && (
                                                        <div style={{ marginTop: '4px', fontSize: '0.8rem', color: '#2563eb', fontWeight: 600 }}>
                                                            <i className="fas fa-ruler-combined" style={{ marginRight: '4px' }}></i>
                                                            Détail découpes: {item.decoupesLineaires.map(d => `${d.nombrePieces} p. × ${d.longueurPieceMetre}m`).join(' + ')}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>{item.produit?.prixAchat ? `${Number(item.produit.prixAchat).toFixed(3)} DT` : '-'}</td>
                                                <td>{item.remarque || '-'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 2: QUOTE RESPONSES */}
                    {activeTab === 'reponses' && (
                        <div className="dp-tab-pane">
                            {reponseSuccessMsg && (
                                <div className="dp-alert dp-alert-success" style={{ marginBottom: '20px' }}>
                                    <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
                                    {reponseSuccessMsg}
                                </div>
                            )}

                            {/* Section A: Saisir une nouvelle réponse / devis fournisseur */}
                            <div className="dp-card" style={{ marginBottom: '24px' }}>
                                <h4>
                                    <i className="fas fa-plus-circle" style={{ color: '#10b981' }}></i>
                                    Enregistrer une Réponse / Devis Fournisseur (Nouvelle Version)
                                </h4>
                                <form onSubmit={handleAddReponseSubmit} className="reponse-form">
                                    <div className="form-row-3">
                                        <div className="dp-input-group">
                                            <label>Fournisseur émetteur *</label>
                                            <select
                                                value={selectedSupplierForReponse}
                                                onChange={(e) => setSelectedSupplierForReponse(e.target.value)}
                                                required
                                            >
                                                <option value="">-- Choisir le fournisseur --</option>
                                                {demande.fournisseurs.map(f => (
                                                    <option key={f._id} value={f._id}>
                                                        {f.nom || f.raisonSociale} ({f.code || ''})
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="dp-input-group">
                                            <label>Réf Devis Fournisseur</label>
                                            <input
                                                type="text"
                                                placeholder="Ex: DEV-2026-981"
                                                value={referenceDevisFournisseur}
                                                onChange={(e) => setReferenceDevisFournisseur(e.target.value)}
                                            />
                                        </div>

                                        <div className="dp-input-group">
                                            <label>Date du Devis</label>
                                            <input
                                                type="date"
                                                value={dateDevisFournisseur}
                                                onChange={(e) => setDateDevisFournisseur(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-row-3" style={{ marginTop: '14px' }}>
                                        <div className="dp-input-group">
                                            <label>Délai de livraison global</label>
                                            <input
                                                type="text"
                                                placeholder="Ex: 7 jours, 2 semaines..."
                                                value={delaiLivraison}
                                                onChange={(e) => setDelaiLivraison(e.target.value)}
                                            />
                                        </div>

                                        <div className="dp-input-group">
                                            <label>Disponibilité globale</label>
                                            <input
                                                type="text"
                                                placeholder="Ex: En stock / Sur commande..."
                                                value={disponibilite}
                                                onChange={(e) => setDisponibilite(e.target.value)}
                                            />
                                        </div>

                                        <div className="dp-input-group">
                                            <label>Joindre le devis PDF / Image</label>
                                            <input
                                                type="file"
                                                accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                                                onChange={(e) => setPieceJointeFile(e.target.files[0])}
                                            />
                                        </div>
                                    </div>

                                    {/* Lines pricing entry */}
                                    <div style={{ marginTop: '20px' }}>
                                        <h5 style={{ margin: '0 0 10px 0', color: '#334155' }}>Prix et Remises proposés par produit:</h5>
                                        <div className="table-responsive">
                                            <table className="dp-table">
                                                <thead>
                                                    <tr>
                                                        <th>Produit</th>
                                                        <th>Quantité</th>
                                                        <th>Prix Unitaire HT *</th>
                                                        <th>Remise 1 (%)</th>
                                                        <th>Remise 2 (%)</th>
                                                        <th>Prix Net HT Unit.</th>
                                                        <th>TVA (%)</th>
                                                        <th>Total TTC Ligne</th>
                                                        <th>Qte Dispo</th>
                                                        <th>Disponibilité</th>
                                                        <th>Délai Produit</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {reponseLignes.map((line) => {
                                                        const pu = Number(line.prixUnitaireHT) || 0;
                                                        const r1 = Number(line.remisePercentage) || 0;
                                                        const r2 = Number(line.remisePercentage2) || 0;
                                                        const tva = Number(line.tvaPercentage) !== undefined ? Number(line.tvaPercentage) : 19;
                                                        const qte = Number(line.quantiteDemandee) || 1;

                                                        const netHTUnit = pu * (1 - r1 / 100) * (1 - r2 / 100);
                                                        const totalLineNetHT = netHTUnit * qte;
                                                        const lineTTC = totalLineNetHT * (1 + tva / 100);

                                                        return (
                                                            <tr key={line.produitId}>
                                                                <td><strong>{line.nomProduit}</strong></td>
                                                                <td>{line.quantiteDemandee}</td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.001"
                                                                        min="0"
                                                                        className="table-input"
                                                                        value={line.prixUnitaireHT}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'prixUnitaireHT', e.target.value)}
                                                                        required
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        max="100"
                                                                        className="table-input"
                                                                        value={line.remisePercentage}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'remisePercentage', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        max="100"
                                                                        className="table-input"
                                                                        value={line.remisePercentage2}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'remisePercentage2', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td><strong>{netHTUnit.toFixed(3)} DT</strong></td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="1"
                                                                        className="table-input"
                                                                        value={line.tvaPercentage}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'tvaPercentage', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td style={{ color: '#059669', fontWeight: 600 }}>
                                                                    {lineTTC.toFixed(3)} DT
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="number"
                                                                        step="any"
                                                                        placeholder="Qté"
                                                                        className="table-input"
                                                                        value={line.quantiteDisponible || ''}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'quantiteDisponible', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="En stock/Sur commande"
                                                                        className="table-input"
                                                                        value={line.disponibiliteProduit || ''}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'disponibiliteProduit', e.target.value)}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="ex: 5j"
                                                                        className="table-input"
                                                                        value={line.delaiProduit}
                                                                        onChange={(e) => handleUpdateReponseLine(line.produitId, 'delaiProduit', e.target.value)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    <div className="dp-input-group" style={{ marginTop: '14px' }}>
                                        <label>Commentaire du fournisseur / Observations</label>
                                        <input
                                            type="text"
                                            placeholder="Commentaires généraux sur l'offre..."
                                            value={commentaire}
                                            onChange={(e) => setCommentaire(e.target.value)}
                                        />
                                    </div>

                                    <button type="submit" className="btn-submit" style={{ marginTop: '16px' }} disabled={submittingReponse}>
                                        {submittingReponse ? (
                                            <><i className="fas fa-spinner fa-spin"></i> Enregistrement...</>
                                        ) : (
                                            <><i className="fas fa-save"></i> Enregistrer cette proposition devis</>
                                        )}
                                    </button>
                                </form>
                            </div>

                            {/* Section B: Liste des réponses enregistrées et historique des versions */}
                            <div className="dp-card">
                                <h4><i className="fas fa-history"></i> Réponses reçues et historique des propositions par fournisseur</h4>
                                {(!demande.reponses || demande.reponses.length === 0) ? (
                                    <p style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                                        Aucune réponse enregistrée pour le moment.
                                    </p>
                                ) : (
                                    <div className="reponses-grouped-list">
                                        {demande.reponses.map((rep, idx) => (
                                            <div key={rep._id || idx} className={`reponse-box ${rep.isLatestVersion ? 'is-latest' : 'is-old'}`}>
                                                <div className="reponse-box-header">
                                                    <div>
                                                        <strong>{rep.fournisseur?.nom || rep.fournisseur?.raisonSociale}</strong>
                                                        <span className={`version-pill ${rep.isLatestVersion ? 'latest' : 'old'}`}>
                                                            Version {rep.versionNumber} {rep.isLatestVersion ? '(Dernière version)' : '(Ancienne version)'}
                                                        </span>
                                                        <span className="reponse-date">
                                                            Reçu le {new Date(rep.dateReponse).toLocaleDateString('fr-FR')}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        {rep.pieceJointeDevis && (
                                                            <a
                                                                href={`${API_URL.replace('/api', '')}${rep.pieceJointeDevis}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="btn-download-pdf"
                                                            >
                                                                <i className="fas fa-file-pdf"></i> Voir Devis PDF
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="reponse-meta-row">
                                                    <span><strong>Réf Devis:</strong> {rep.referenceDevisFournisseur || 'N/A'}</span>
                                                    <span><strong>Délai:</strong> {rep.delaiLivraison || 'Non spécifié'}</span>
                                                    <span><strong>Dispo:</strong> {rep.disponibilite || 'Non spécifiée'}</span>
                                                    <span><strong>Total TTC Devis:</strong> <strong style={{ color: '#059669' }}>{Number(rep.montantTotalTTC || 0).toFixed(3)} DT</strong></span>
                                                </div>

                                                {rep.commentaire && (
                                                    <div className="reponse-comment">
                                                        <i className="far fa-comment"></i> {rep.commentaire}
                                                    </div>
                                                )}

                                                <table className="dp-table mini-table" style={{ marginTop: '10px' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Produit</th>
                                                            <th>Prix Brut HT</th>
                                                            <th>Remise 1</th>
                                                            <th>Remise 2</th>
                                                            <th>Prix Net HT</th>
                                                            <th>TVA</th>
                                                            <th>Prix TTC</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {(rep.lignes || []).map((l, i) => (
                                                            <tr key={i}>
                                                                <td>{l.produit?.nomProduit || 'Produit'}</td>
                                                                <td>{Number(l.prixUnitaireHT || 0).toFixed(3)} DT</td>
                                                                <td>{l.remisePercentage || 0}%</td>
                                                                <td>{l.remisePercentage2 || 0}%</td>
                                                                <td><strong>{Number(l.prixNetHT || 0).toFixed(3)} DT</strong></td>
                                                                <td>{l.tvaPercentage || 19}%</td>
                                                                <td style={{ color: '#059669', fontWeight: 600 }}>{Number(l.prixTTC || 0).toFixed(3)} DT</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 3: SIDE-BY-SIDE COMPARISON MATRIX */}
                    {activeTab === 'comparaison' && (
                        <div className="dp-tab-pane">
                            <div className="dp-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h4 style={{ margin: 0 }}>
                                        <i className="fas fa-balance-scale" style={{ color: '#3b82f6', marginRight: '8px' }}></i>
                                        Matrice comparative des offres reçues par produit
                                    </h4>
                                    <span style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                        Toutes les versions de chaque fournisseur sont conservées et comparables.
                                    </span>
                                </div>

                                {matrix.length === 0 ? (
                                    <p style={{ color: '#94a3b8' }}>Aucune donnée comparative disponible.</p>
                                ) : (
                                    matrix.map((row, rIdx) => (
                                        <div key={rIdx} className="comparison-product-card">
                                            <div className="comparison-product-header">
                                                <div>
                                                    <strong style={{ fontSize: '1.05rem', color: '#0f172a' }}>{row.produit?.nomProduit}</strong>
                                                    <span style={{ color: '#64748b', marginLeft: '12px' }}>Réf: {row.produit?.reference}</span>
                                                </div>
                                                <div className="qty-demand-badge">
                                                    Quantité demandée: <strong>{row.quantiteDemandee}</strong>
                                                </div>
                                            </div>

                                            <div className="comparison-grid">
                                                {row.offers.length === 0 ? (
                                                    <p style={{ color: '#94a3b8', fontStyle: 'italic', padding: '10px' }}>
                                                        Aucune offre reçue pour ce produit.
                                                    </p>
                                                ) : (
                                                    row.offers.map((off, oIdx) => {
                                                        const isSelected = selectedOffresMap[row.produit?._id]?.reponseId === off.reponseId;
                                                        return (
                                                            <div
                                                                key={oIdx}
                                                                className={`offer-comparison-tile ${off.isLatestVersion ? 'latest' : 'old'} ${isSelected ? 'selected' : ''}`}
                                                            >
                                                                <div className="tile-supplier-header">
                                                                    <strong>{off.fournisseur?.nom || off.fournisseur?.raisonSociale}</strong>
                                                                    <span className="version-tag">
                                                                        v{off.versionNumber} {off.isLatestVersion ? '(Actuel)' : '(Ancien)'}
                                                                    </span>
                                                                </div>

                                                                {off.ligne ? (
                                                                    <div className="tile-body">
                                                                        <div className="price-big-display">
                                                                            <span className="net-price">{Number(off.ligne.prixNetHT || 0).toFixed(3)} DT HT</span>
                                                                            <span className="gross-price">
                                                                                P.U Brut: {Number(off.ligne.prixUnitaireHT || 0).toFixed(3)} DT
                                                                            </span>
                                                                        </div>

                                                                        <div className="tile-detail-row">
                                                                            <span>Remises:</span>
                                                                            <strong>
                                                                                {off.ligne.remisePercentage || 0}%
                                                                                {off.ligne.remisePercentage2 > 0 ? ` + ${off.ligne.remisePercentage2}%` : ''}
                                                                            </strong>
                                                                        </div>

                                                                        <div className="tile-detail-row">
                                                                            <span>Total Net HT:</span>
                                                                            <strong>{Number(off.ligne.montantTotalNetHT || 0).toFixed(3)} DT</strong>
                                                                        </div>

                                                                        <div className="tile-detail-row">
                                                                            <span>Total TTC:</span>
                                                                            <strong style={{ color: '#059669' }}>{Number(off.ligne.prixTTC || 0).toFixed(3)} DT</strong>
                                                                        </div>

                                                                        <div className="tile-detail-row">
                                                                            <span>Délai:</span>
                                                                            <span>{off.ligne.delaiProduit || off.delaiLivraisonGlobal || 'N/A'}</span>
                                                                        </div>

                                                                        <button
                                                                            className={`btn-select-offer ${isSelected ? 'selected' : ''}`}
                                                                            onClick={() => handleSelectOfferForProduct(row.produit?._id, off)}
                                                                        >
                                                                            {isSelected ? (
                                                                                <><i className="fas fa-check-circle"></i> Offre retenue</>
                                                                            ) : (
                                                                                <>Choisir cette offre</>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                ) : (
                                                                    <div className="tile-body empty">
                                                                        <span>Pas de prix proposé</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* TAB 4: SELECTION & ONE-CLICK CONVERSION */}
                    {activeTab === 'selection' && (
                        <div className="dp-tab-pane">
                            {conversionSuccessMsg && (
                                <div className="dp-alert dp-alert-success" style={{ marginBottom: '20px' }}>
                                    <i className="fas fa-check-circle" style={{ marginRight: '8px' }}></i>
                                    {conversionSuccessMsg}
                                </div>
                            )}

                            <div className="dp-card" style={{ marginBottom: '24px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h4>
                                        <i className="fas fa-check-double" style={{ color: '#10b981', marginRight: '8px' }}></i>
                                        Synthèse de la sélection manuelle des offres retenues
                                    </h4>
                                    <button
                                        className="btn-submit"
                                        onClick={handleSaveSelections}
                                        disabled={savingSelection}
                                    >
                                        {savingSelection ? 'Enregistrement...' : 'Valider la sélection'}
                                    </button>
                                </div>

                                <table className="dp-table" style={{ marginTop: '16px' }}>
                                    <thead>
                                        <tr>
                                            <th>Produit</th>
                                            <th>Fournisseur Retenu</th>
                                            <th>Version Offre</th>
                                            <th>Quantité</th>
                                            <th>Prix Net HT Unit.</th>
                                            <th>Total HT</th>
                                            <th>Total TTC</th>
                                            <th>Statut</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {demande.produits.map((item) => {
                                            const pId = item.produit?._id || item.produit;
                                            const selected = selectedOffresMap[pId];

                                            return (
                                                <tr key={pId}>
                                                    <td><strong>{item.produit?.nomProduit}</strong></td>
                                                    <td>
                                                        {selected ? (
                                                            <strong>
                                                                {demande.fournisseurs.find(f => String(f._id) === String(selected.fournisseurId))?.nom || 'Fournisseur'}
                                                            </strong>
                                                        ) : (
                                                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Non sélectionné</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {selected ? <span className="version-tag">Version {selected.versionNumber}</span> : '-'}
                                                    </td>
                                                    <td>{item.quantite}</td>
                                                    <td>{selected ? `${Number(selected.prixNetHT || 0).toFixed(3)} DT` : '-'}</td>
                                                    <td>{selected ? `${(Number(selected.prixNetHT || 0) * item.quantite).toFixed(3)} DT` : '-'}</td>
                                                    <td>
                                                        {selected ? (
                                                            <strong style={{ color: '#059669' }}>
                                                                {((Number(selected.prixNetHT || 0) * item.quantite) * (1 + (selected.tvaPercentage || 19) / 100)).toFixed(3)} DT
                                                            </strong>
                                                        ) : '-'}
                                                    </td>
                                                    <td>
                                                        {selected ? (
                                                            <span className="dp-status-badge statut-terminee">Retenu</span>
                                                        ) : (
                                                            <span className="dp-status-badge statut-brouillon">En attente</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {/* Section: One-click Conversion Buttons */}
                            <div className="dp-card">
                                <h4>
                                    <i className="fas fa-file-contract" style={{ color: '#3b82f6', marginRight: '8px' }}></i>
                                    Transformation des offres retenues en Devis / Bon de Commande / BL Fournisseur
                                </h4>
                                <p style={{ color: '#475569', fontSize: '0.9rem' }}>
                                    Une fois les offres retenues validées, choisissez le type de document à générer pour chaque fournisseur dans le workflow officiel (sans aucune re-saisie de prix).
                                </p>

                                <div className="conversion-actions-bar">
                                    {demande.fournisseurs.map(f => {
                                        const countSelectedForSupplier = Object.values(selectedOffresMap).filter(
                                            s => String(s.fournisseurId) === String(f._id)
                                        ).length;

                                        return (
                                            <div key={f._id} className="conversion-supplier-card">
                                                <div>
                                                    <strong>{f.nom || f.raisonSociale}</strong>
                                                    <div style={{ color: '#64748b', fontSize: '0.85rem' }}>
                                                        {countSelectedForSupplier} produit(s) sélectionné(s)
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                    <select
                                                        id={`doc-type-${f._id}`}
                                                        className="table-input"
                                                        style={{ width: 'auto', padding: '8px 12px', borderRadius: '8px' }}
                                                        defaultValue="bon_commande"
                                                    >
                                                        <option value="bon_commande">Bon de Commande</option>
                                                        <option value="devis">Devis Fournisseur</option>
                                                        <option value="bon_livraison">Bon de Livraison</option>
                                                    </select>
                                                    <button
                                                        className="btn-convert-bc"
                                                        disabled={countSelectedForSupplier === 0 || converting}
                                                        onClick={() => {
                                                            const selectEl = document.getElementById(`doc-type-${f._id}`);
                                                            const targetType = selectEl ? selectEl.value : 'bon_commande';
                                                            handleConvertBC(f._id, targetType);
                                                        }}
                                                    >
                                                        {converting ? (
                                                            <><i className="fas fa-spinner fa-spin"></i> Génération...</>
                                                        ) : (
                                                            <><i className="fas fa-file-export"></i> Générer</>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default DetailDemandePrixPage;
